import React, { useState } from 'react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Typography from '@ui/Typography';
import Switch from '@ui/Switch';
import { defaults, pick } from 'lodash-es';
import StyledContainedButton from '../../Styled/StyledContainedButton';

const schema = yup.object().shape({
  profileVisible: yup
    .boolean()
    .oneOf([true, false], 'Make profile visible')
    .notRequired(),
  skillsVisible: yup
    .boolean()
    .oneOf([true, false], 'Make skills visible')
    .notRequired(),
  contactInformationVisible: yup
    .boolean()
    .oneOf([true, false], 'Make contact information visible')
    .notRequired(),
  causesSupportedVisible: yup
    .boolean()
    .oneOf([true, false], 'Make causes supported visible')
    .notRequired(),
  locationVisible: yup
    .boolean()
    .oneOf([true, false], 'Make location visible')
    .notRequired(),
});

function EditProfilePrivacyStep(props) {
  const { userProfile, onSubmit, onBack, formData, onNext, saving } = props;

  const [profileVisible, setProfileVisible] = useState(
    userProfile ? userProfile.profileVisible : false,
  );

  const defaultValues = defaults(
    pick(formData, [
      'profileVisible',
      'skillsVisible',
      'contactInformationVisible',
      'causesSupportedVisible',
      'locationVisible',
    ]),
    {
      profileVisible: userProfile ? userProfile.profileVisible : true,
      skillsVisible: userProfile ? userProfile.skillsVisible : true,
      contactInformationVisible: userProfile
        ? userProfile.contactInformationVisible
        : true,
      causesSupportedVisible: userProfile
        ? userProfile.causesSupportedVisible
        : true,
      locationVisible: userProfile ? userProfile.locationVisible : true,
    },
  );

  const form = useForm({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    trigger,
  } = form;

  const handleFormSubmit = async (data) => {
    data.profileVisible = profileVisible;
    onSubmit(data, 'Privacy Settings');
  };

  // Function to toggle the profile visibility
  const toggleProfileVisibility = () => {
    setProfileVisible(!profileVisible);
  };

  return (
    <div className="flex w-full flex-1 flex-col gap-12">
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <Typography variant="h4">Make Your Profile Visible</Typography>
          <Switch
            name="profileVisible"
            control={control}
            onChange={toggleProfileVisibility}
            checked={profileVisible}
            error={errors.profileVisible?.message}
          />
        </div>

        {profileVisible && (
          // Render other options only when profileVisible is true
          <>
            <div className="flex items-center justify-between">
              <Typography variant="h4">Make Your Skills Visible</Typography>
              <Controller
                name="skillsVisible"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Switch {...rest} error={errors.skillsVisible?.message} />
                )}
              />
            </div>

            <div className="flex items-center justify-between">
              <Typography variant="h4">
                Make Your Contact Information Visible
              </Typography>
              <Controller
                name="contactInformationVisible"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Switch
                    {...rest}
                    error={errors.contactInformationVisible?.message}
                  />
                )}
              />
            </div>

            <div className="flex items-center justify-between">
              <Typography variant="h4">
                Make Your Causes Supported Visible
              </Typography>
              <Controller
                name="causesSupportedVisible"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Switch
                    {...rest}
                    error={errors.causesSupportedVisible?.message}
                  />
                )}
              />
            </div>

            <div className="flex items-center justify-between">
              <Typography variant="h4">Make Your Location Visible</Typography>
              <Controller
                name="locationVisible"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Switch {...rest} error={errors.locationVisible?.message} />
                )}
              />
            </div>
          </>
        )}

        <div className="flex-rows flex w-full justify-end gap-4">
          {isValid ? (
            <div className="flex-flow-col">
              <StyledContainedButton
                variant="filled"
                color="primary"
                type="submit"
                className="mt-2"
                loading={saving}>
                Save
              </StyledContainedButton>
            </div>
          ) : (
            <div className="flex-flow-col">
              <StyledContainedButton
                disabled={true}
                variant="outlined"
                color="primary"
                type="submit"
                className="mt-2"
                loading={saving}>
                Save
              </StyledContainedButton>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

export default EditProfilePrivacyStep;
